import React, { FC, useEffect, useMemo, useState } from 'react';
import { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MUIAccordion, { AccordionProps } from '@mui/material/Accordion';
import MUIAccordionDetails from '@mui/material/AccordionDetails';
import MUIAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import styled from '@mui/material/styles/styled';

import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwInitialsIndicator from '../../../components/TwInitialsIndicator/TwInitialsIndicator';

import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import StringUtils from '../../../utils/stringUtils';
import LoadUtils from '../../../utils/loadUtils';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import useFormatUrlsInText from '../../../hooks/useFormatUrlText';
import { useParams } from 'react-router-dom';
import NumberUtils from '../../../utils/numberUtils';
import TwGrossPayTooltip from '../../../components/TwGrossPayTooltip/TwGrossPayTooltip';
import {
  DROP_ZONE_HIGHLIGHTED_TEXT,
  MAX_FILE_SIZE
} from '../FullPublicRouteDetail/FullPublicRouteDetail';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import SegmentRouteInfo from '../../../components/TwLoadDetail/components/SegmentRouteInfo/SegmentRouteInfo';
import Stack from '@mui/material/Stack';
import { LOAD_TYPE, LoadDocumentType } from '../../../redux/api/load/load.model';
import { PublicLoadTenant } from '../../../providers/Guest/Guest.model';
import { TabPanelProps } from '../PublicLoad';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TwAlert from '../../../components/TwAlerts/TwAlerts';
import DetailFile from '../../../components/TwLoadDetail/DetailFile/DetailFile';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import useAuth from '../../../providers/Auth/Auth.provider';
import {
  TwLoadDetailTab,
  TwLoadDetailTabsGroup
} from '../../../components/TwLoadDetail/components/LoadTabs/TwLoadDetailTabs';
import Chip from '@mui/material/Chip/Chip';
import TwDispatchNotes from '../../../components/TwDispatchNotes/TwDispatchNotes';
import useScrollToView from '../../../hooks/useScrollToView';
import DriverPayUtils from '../../../utils/driverPayUtils';
import {
  StopStatus,
  Trip,
  TripDriver,
  TRIP_STATUS,
  TripStop,
  TripStatusColor,
  TripDriverAckStatus
} from '../../../redux/api/trips/trips.model';
import { Order, OrderAttachment, SharedLinkTenant } from '../../../redux/api/orders/orders.model';
import TwOrderTripsDocViewer from '../../../components/TwOrderTripsDocViewer/TwOrderTripsDocViewer';
import TwUploadOrderTripFiles from '../../../components/TwUploadOrderTripFiles/TwUploadOrderTripFiles';
import TwDeleteOrderTripDoc from '../../../components/TwUploadOrderTripFiles/TwDeleteOrderTripDoc';
import TwScanner from '../../../components/TwScanner/TwScanner';
import {
  useDeleteOrderTripDocumentByTokenMutation,
  useGetDispatchNotesQuery
} from '../../../redux/api/orders/ordersApi';
import UploadBol from '../../UploadBol/UploadBol';
import useStopStatusProgression from '../../../hooks/useStopStatusProgression';
import { TwEquipmentField } from '../../../components/TwEquipmentField/TwEquipmentField';
import { EquipmentType } from '../../../redux/api/equipment/equipment.model';
import { USER_ROLE } from '../../../providers/User/User.model';
import { TwLoadCommodityReadOnly } from '../../../components/Commodities/TwLoadCommodities/TwLoadCommodities';
import { EntityType } from '../../../redux/Global.model';
import { useAppSelector } from '../../../redux/hooks';
import TwLoadTags from '../../../components/TwLoadTag/TwLoadTag';
import TwBadge from '../../../components/TwBadge/TwBadge';
import { ClientResponse } from '../../../redux/api/customer/customer.model';
import TwTotalMilage from '../../../components/TwLoadDetail/components/TotalMilage/TwTotalMilage';
import TwExpensesTab from '../../../components/TwExpensesTab/TwExpensesTab';
import TwDocumentsToGenerateTrip from '../../../components/TwLoadDetail/components/DocumentsToGenerateTrip/TwDocumentsToGenerateTrip';

import FileUtils from '../../../utils/fileUtils';
import {
  DriverAckType,
  OwnerOperatorDriverAckType,
  TenantDriverSettings
} from '../../../redux/api/tenantDriverSettings/tenantDriverSettings.model';
import { DRIVER_TYPE } from '../../../redux/api/driver/driver.model';
import { TwAcceptRejectButtons } from '../../../components/TwAcceptRejectButtons/TwAcceptRejectButtons';
import { TwRejectedTrip } from '../../../components/TwRejectedTrip/TwRejectedTrip';
export interface TripPublicDetailProps {
  order: Order | null;
  trip: Trip;
  primaryClient: ClientResponse | null;
  tenant?: PublicLoadTenant | SharedLinkTenant;
  driver?: { id: string; name: string };
  isDriverAssignedToTrip: boolean;
  reloadData?: () => Promise<void>;
  segmentOrder: number;
  isBolWarningShown?: boolean;
  onStatusProgression?: () => void;
  stops: TripStop[];
  stopStartOrder?: number;
  customersCount?: number;
}

interface IExpandedItems {
  [key: string]: boolean;
}

const EXPANDED_ITEMS: IExpandedItems = {
  route: true,
  instructions: true,
  equipment: true,
  attachments: true,
  overview: true,
  photos: true,
  commodity: true
};

const isWebInFlutter = (): boolean => {
  return navigator.userAgent.includes('Flutter');
};
const TripPublicDetail: FC<TripPublicDetailProps> = ({
  order,
  trip,
  primaryClient,
  tenant,
  driver,
  isDriverAssignedToTrip,
  reloadData,
  segmentOrder,
  stops,
  onStatusProgression,
  stopStartOrder = 1,
  customersCount
}) => {
  const {
    data: { userData }
  } = useAuth();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const {
    enableDynamicWebTwain,
    enableDriverGrossPayEstimate,
    enableFlexibleTagging,
    enableDriverAcknowledge
  } = useFeatureFlags();
  const isLtlEnabled = useAppSelector((state) => state.user.isLtlEnabled);
  const formatUrlsInText = useFormatUrlsInText();
  const { addNotification } = useNotifications();
  const [scrollIntoView] = useScrollToView();
  const [deleteDocumentByToken] = useDeleteOrderTripDocumentByTokenMutation();
  const { token } = useParams();
  const { progressStopStatusForStops } = useStopStatusProgression();

  const dispatchNotesRef = React.useRef<HTMLDivElement>(null);

  const [loadTabs, setLoadTabs] = useState<number>(0);
  const [isTwScannerOpen, setIsTwScannerOpen] = useState<boolean>(false);
  const [browserOnline, setBrowserOnline] = useState<boolean>(true);
  const [isEnabledGrossPayOnDispatch, setIsEnabledGrossPayOnDispatch] = useState(false);
  const [isEnabledCarrier, setIsEnabledCarrier] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [isBolUploadOpen, setIsBolUploadOpen] = useState<boolean>(false);
  const [isBolWarningShown, setIsBolWarningShown] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState<boolean>(false);
  const [expandedItems, setExpandedItems] = useState<IExpandedItems>(EXPANDED_ITEMS);
  const [fileToUpload, setFileToUpload] = useState<File>();
  const [fileUploadErrors, setFileUploadErrors] = useState<string[]>([]);
  const [isEnabledSeeDocs, setIsEnabledSeeDocs] = useState<boolean>(true);
  const [docViewOpen, setDocViewOpen] = useState(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<OrderAttachment | null>(null);
  const [segmentStops, setSegmentStops] = useState<TripStop[]>([]);
  const [matchesMobileOrFlutterApp, setMatchesMobileOrFlutterApp] = useState<boolean>(false);
  const [driversEditEquipmentEnabled, setDriversEditEquipmentEnabled] = useState<boolean>(false);
  const [commoditiesEnabled, setCommoditiesEnabled] = useState(false);
  const [driversToEditMilesEnabled, setIsdriversToEditMilesEnabled] = useState<boolean>(false);
  const [isEmptyMilesEnabled, setIsEmptyMilesEnabled] = useState<boolean>(false);
  const [isHideDriverInfoEnabled, setIsHideDriverInfoEnabled] = useState<boolean>(false);

  // driver ack
  const [showAcceptRejectSection, setShowAcceptRejectSection] = useState<boolean>(false);
  const [isDriverAckSettingOn, setIsDriverAckSettingOn] = useState<boolean>(false);
  const [tenantDriverSettings, setTenantDriverSettings] = useState<Pick<
    TenantDriverSettings,
    'company_driver_ack_type' | 'owner_operator_driver_ack_type'
  > | null>(null);

  const { data: notes } = useGetDispatchNotesQuery({
    orderId: order?.id,
    tripId: trip.id,
    token,
    order: matchesMobileOrTablet ? 'asc' : 'desc'
  });

  useEffect(() => {
    setMatchesMobileOrFlutterApp(matchesMobileOrTablet || isWebInFlutter());
  }, []);

  useEffect(() => {
    if (!tenant) return;

    const { tenantSettings, tenantDriverSettings } = tenant;
    setIsEnabledSeeDocs(tenantSettings?.allow_drivers_to_view_documents);

    setIsEnabledGrossPayOnDispatch(tenantSettings['gross-pay-on-dispatch']);
    setIsEnabledCarrier(tenantSettings['multiple-carriers']);
    setDriversEditEquipmentEnabled(tenantSettings['allow-drivers-to-edit-truck-and-trailer']);
    setCommoditiesEnabled(tenantSettings['load-commodities']);
    setIsdriversToEditMilesEnabled(tenantSettings['allow-drivers-to-edit-miles']);
    setIsEmptyMilesEnabled(tenantSettings['empty-miles']);
    setIsDriverAckSettingOn(tenantSettings['driver-dispatch-ack']);
    setTenantDriverSettings(tenantDriverSettings);
    setIsHideDriverInfoEnabled(tenantSettings['hide-driver-info-customer-link']);
  }, [tenant]);

  useEffect(() => {
    const online = navigator.onLine;
    if (!online) {
      setBrowserOnline(false);
    } else {
      setBrowserOnline(true);
    }
  }, [navigator.onLine]);

  const handleAccordionExpandedChange = (key: string): void => {
    const currentExpanded = expandedItems[key];
    setExpandedItems((currentExpandedItems) => ({
      ...currentExpandedItems,
      [key]: !currentExpanded
    }));
  };

  const isDocumentVisible = (
    docVisibility: boolean | undefined,
    type: LoadDocumentType
  ): boolean => {
    if (type === LoadDocumentType.tripManifest) {
      return isDriverAssignedToTrip ? docVisibility || false : true;
    }

    return !isDriverAssignedToTrip || docVisibility || false;
  };

  const tripAttachments = useMemo(() => {
    if (isLtlEnabled) {
      return trip?.trip_attachments || [];
    } else {
      const orderAttachments =
        order?.order_attachments?.filter((doc) => {
          if ((doc.trip_id && doc.trip_id === trip.id) || !doc.trip_id) {
            return doc;
          }
        }) || [];

      return orderAttachments.filter((doc) => isDocumentVisible(doc.visibility, doc.type));
    }
  }, [trip?.trip_attachments, isLtlEnabled, order?.order_attachments]);

  const generatedDocuments = useMemo(() => {
    if (isLtlEnabled) {
      return trip?.trip_attachments || [];
    } else {
      const tripAttachments = trip?.trip_attachments;

      return tripAttachments?.filter((doc) => isDocumentVisible(doc.visibility, doc.type));
    }
  }, [trip?.trip_attachments, isLtlEnabled, order?.order_attachments]);

  // We need to filter docs to preview with visibility for drivers,
  // otherwise drivers can see files while on preview clicking on arrow right,
  // customer or anyone else that is not a driver can see all files.
  const filesToPreview = useMemo(() => {
    let filespreview: any = [];

    if (driver && driver.id && isDriverAssignedToTrip) {
      const docsfiltered = tripAttachments?.filter((doc) => doc.visibility);
      const generatedDocsfiltered = generatedDocuments?.filter((doc) => doc.visibility);
      filespreview = [...(docsfiltered ?? []), ...(generatedDocsfiltered ?? [])];
    } else {
      filespreview = [...(tripAttachments ?? [])];
    }

    return filespreview;
  }, [driver, isDriverAssignedToTrip, tripAttachments, generatedDocuments]);

  const handleChangeUploadDocument = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e?.target?.files !== null) {
      if (e.target.files[0].size > MAX_FILE_SIZE) {
        logRocketTrack('Upload document', {
          action: 'Upload document on public link',
          severity: 'low',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: 'Error: maximum attachment size 15 mb',
          variant: 'error',
          position: 'bottomCenter'
        });
        return;
      }
      setFileToUpload(e.target.files[0]);
      e.target.value = '';
      setDocumentsOpen(true);
    }
  };

  const handleDocumentsUpload = async (): Promise<void> => {
    reloadData && (await reloadData());
  };

  const handleDocViewerOpen = (
    isDocumentVisible: boolean | undefined,
    doc: OrderAttachment
  ): void => {
    if (isDocumentVisible || (isDocumentVisible === undefined && isEnabledSeeDocs)) {
      setDocViewOpen(true);
      setSelectedDocument(doc);
    }
  };

  const handleDownloadDoc = async (doc: OrderAttachment): Promise<void> => {
    if (doc?.attachment?.s3_signed_download_url) {
      FileUtils.fetchAndDownloadFileLinkClick(doc.attachment.s3_signed_download_url);
    } else {
      addNotification({
        message: 'Error downloading document',
        variant: 'error'
      });
    }
  };

  const handleBolUploadClose = (): void => {
    setIsBolUploadOpen(false);
    // handleStatusProgression(); TODO 0&T
  };

  useEffect(() => {
    if (trip?.stops && trip.stops.length > 0) {
      setSegmentStops(trip.stops.map((stop) => ({ ...stop, sort_order: stop.sort_order + 1 })));
    }
  }, [trip]);

  useEffect(() => {
    if (loadTabs === 3 && matchesMobileOrTablet) {
      scrollIntoView(dispatchNotesRef, 'start');
    }
  }, [loadTabs, matchesMobileOrTablet]);

  const handleStopStatusProgression = async (): Promise<void> => {
    if (!token || !trip.id || trip.status === TRIP_STATUS.DELIVERED) return;

    const nextStopStatusInfo = LoadUtils.getNextStopStatus(segmentStops);

    if (!nextStopStatusInfo) return;
    const [_, nextStopStatus] = nextStopStatusInfo;

    try {
      setLoading(true);

      progressStopStatusForStops(trip.id, segmentStops, trip.status || TRIP_STATUS.DRAFT, [], token)
        .then(() => {
          reloadData && reloadData();
          onStatusProgression && onStatusProgression();
          addNotification({
            message: `Stop marked as ${LoadUtils.getStopStatusReadableText(
              nextStopStatus as StopStatus
            )}`,
            variant: 'success'
          });
        })
        .catch((error) => {
          logRocketTrack('Update stop status from driver link', {
            action: 'Update stop status from driver link',
            severity: 'high',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });

          let message = `Something went wrong. Please try again.`;
          let hasReloadAction = false;

          if (error.status && error.status >= 400 && error.status < 500 && error.data?.message) {
            message = error.data.message;
            // Check if message contains "reload" (case insensitive)
            hasReloadAction = message.toLowerCase().includes('reload');
          } else {
            // log the error
            console.log('Unexpected error', error);
          }

          const reloadPage = (): void => {
            window.location.reload();
          };
          addNotification({
            message,
            variant: 'error',
            ...(hasReloadAction && {
              actionOptions: {
                action: 'reload',
                onActionClick: (): void => {
                  reloadPage();
                }
              }
            })
          });
        });
    } catch (error) {
      logRocketTrack('Update stop status from driver link', {
        action: 'Update stop status from driver link',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name
      });
      console.log('Unexpected error', error);

      addNotification({
        message: `Something went wrong. Please try again.`,
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const shouldShowGrossPay = (dr: TripDriver): boolean => {
    const isGrossPaySettingsOn: boolean =
      enableDriverGrossPayEstimate && isEnabledGrossPayOnDispatch;

    const isGrossPayPresent =
      !!dr.driver_pay_override ||
      DriverPayUtils.getOtherPayItemsTotalAmount(dr.trip_driver_pay_other_payment || []) > 0;
    const isDriverLink = !!driver && driver.id === dr.id;
    return isGrossPaySettingsOn && isGrossPayPresent && isDriverLink;
  };

  const statusLabelButton = useMemo(() => {
    if (segmentStops.length > 0) {
      return (
        LoadUtils.getNextStopLevelStatusLabel(
          segmentStops,
          stopStartOrder == 0 ? 0 : stopStartOrder - 1
        ) || ''
      );
    } else {
      return '';
    }
  }, [segmentStops]);

  // Doc and photo delete
  const handleCloseDeleteDoc = (): void => {
    setShowDelete(false);
    setSelectedDocument(null);
  };

  const handleBolUpload = async (): Promise<void> => {
    closeScanner(true);
    // progress status if scanner was opened for BOL upload
    if (isBolWarningShown) {
      setIsBolUploadOpen(false);
      // await handleStatusProgression(); TODO O&T
    }
    reloadData && reloadData();
  };

  const handleDeleteDoc = (): void => {
    order?.id &&
      token &&
      selectedDocument &&
      deleteDocumentByToken({ id: selectedDocument.id, token })
        .unwrap()
        .then(() => {
          reloadData && reloadData();
          setShowDelete(false);
        });
  };

  const handleLoadDetailsTabChange = (_event: React.SyntheticEvent, newValue: number): any => {
    setLoadTabs(newValue);
  };

  const openScanner = (): void => {
    if (!browserOnline) {
      logRocketTrack('Customer public link', {
        action: 'Network error',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Network error. Please connect to the internet and try again.',
        variant: 'error',
        position: 'bottomCenter'
      });

      return;
    }
    setIsTwScannerOpen(true);
    // close BOL upload modal if scanner was opened from BOL upload
    setIsBolUploadOpen(false);
  };

  const closeScanner = (documentUploaded: boolean): void => {
    setIsTwScannerOpen(false);

    if (!documentUploaded) {
      logRocketTrack('Upload', {
        action: 'Upload document on public link',
        severity: 'medium',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'No document uploaded',
        position: 'bottomCenter',
        variant: 'error'
      });
    }

    // progress if scanner was opened for BOL upload
    /* TODO: if (isBolWarningShown) {
      handleStatusProgression();
      setIsBolUploadOpen(false);
    } */
  };

  const readableId = order?.readable_id
    ? `${order?.readable_id}-${segmentOrder + 1}`
    : trip?.readable_id;

  const showOrderTag = (orderId: string | number): string | number => {
    if (!trip) return '';
    const order = trip.orders?.find((o) => o.id === orderId);
    return order ? order.readable_id : '';
  };

  const showCommodities = useMemo(() => {
    if (!commoditiesEnabled) return false;

    if (isLtlEnabled && trip) {
      return trip.trip_commodities ? trip.trip_commodities.length > 0 : false;
    }
    if (!isLtlEnabled && order) {
      return order.order_commodities?.length > 0;
    }
  }, [commoditiesEnabled, order?.order_commodities, trip, isLtlEnabled]);

  const getCommodities = (): any => {
    if (isLtlEnabled && trip) {
      return trip.trip_commodities || [];
    } else {
      return order?.order_commodities || [];
    }
  };

  const readableTripNumber = useMemo((): string => {
    const orderReadableId = order ? order.readable_id : trip ? trip.order_readable_id : undefined;
    const tripOrder = trip.sort_order;
    const tripReadableId = trip.readable_id;

    return !isLtlEnabled && orderReadableId
      ? `${orderReadableId}-${tripOrder + 1}`
      : tripReadableId
      ? `${tripReadableId}`
      : '';
  }, [trip, order]);

  const showGeneralDataSectionDesktop = (): JSX.Element => {
    if (driver && driver.id && isDriverAssignedToTrip) {
      return (
        <Box component="div" className="load-data-container__general-data">
          <TwLoadDetailTabsGroup
            value={loadTabs}
            onChange={handleLoadDetailsTabChange}
            aria-label="TwTabs"
            role="TwTabs"
          >
            <TwLoadDetailTab label="Trip overview" />
            <TwLoadDetailTab
              label="Expenses"
              icon={<Chip sx={{ height: '20px' }} label={trip.other_expenses_count_by_role || 0} />}
              iconPosition="end"
            />
            <TwLoadDetailTab
              label="Notes"
              icon={<Chip sx={{ height: '20px' }} label={notes?.length || 0} />}
              iconPosition="end"
            />
          </TwLoadDetailTabsGroup>
          <TabPanel id="load-detail-tab" value={loadTabs} index={0}>
            <>
              <Box component="div" className="title">
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-start"
                  alignItems="center"
                  sx={{ width: '100%', marginBottom: '20px' }}
                >
                  <span>Route</span>
                </Stack>
              </Box>

              {trip && (
                <SegmentRouteInfo
                  segment={trip}
                  allSegments={order?.trips || [trip]}
                  startOrder={stopStartOrder}
                  loadId={order?.id || ''}
                  showStatusUpdateInput={false}
                  showAddSignatureAction={!!driver && isDriverAssignedToTrip}
                  isCustomerLink={!driver || !isDriverAssignedToTrip}
                  publicLoadToken={token}
                  reloadData={(): void => {
                    reloadData && reloadData();
                  }}
                />
              )}

              {showCommodities && (
                <TwLoadCommodityReadOnly
                  commodities={getCommodities()}
                  entity={EntityType.TRIP}
                  disableActions={true}
                  overlayView={isLtlEnabled}
                />
              )}

              <>
                <Box component="div" className="title">
                  Trip notes
                </Box>
                <Box component="div" className="special-instructions">
                  {formatUrlsInText(trip?.instructions || '')}
                </Box>
              </>

              <Box component="div" className="title">
                Equipment
              </Box>
              <Box component="div" className="equipment">
                <TwEquipmentField
                  driversEditEquipmentEnabled={driversEditEquipmentEnabled}
                  equipmentType={EquipmentType.truck}
                  userRole={USER_ROLE.DRIVER}
                  segmentId={trip.id}
                  token={token}
                />
                <TwEquipmentField
                  driversEditEquipmentEnabled={driversEditEquipmentEnabled}
                  equipmentType={EquipmentType.trailer}
                  userRole={USER_ROLE.DRIVER}
                  segmentId={trip.id}
                  token={token}
                />
              </Box>

              <Box>
                <Box component="div" className="title">
                  {`${tripAttachments.length || 0} Attachments`}
                </Box>
                <Box component="div" className="attachments">
                  {driver && driver.id && isDriverAssignedToTrip && (
                    <>
                      <Box
                        className="attachments--container-2"
                        onClick={(): void =>
                          matchesMobileOrFlutterApp ? openScanner() : setDocumentsOpen(true)
                        }
                      >
                        <CloudUploadIcon
                          className="attachments--icon"
                          sx={{ marginRight: '10px' }}
                        />
                        <p className="attachments--help-text">
                          <span className="attachments--highlited-text">
                            {DROP_ZONE_HIGHLIGHTED_TEXT}
                          </span>
                        </p>
                      </Box>

                      {fileUploadErrors.length > 0 &&
                        fileUploadErrors.map((error, i) => <TwAlert key={i} message={error} />)}
                    </>
                  )}

                  <TwDocumentsToGenerateTrip
                    files={generatedDocuments}
                    tripId={trip.id}
                    isDriverOrCarrier={true}
                    onDownload={(file): void => {
                      handleDownloadDoc(file);
                    }}
                    onClick={(id): void => {
                      const document = generatedDocuments.find((doc) => doc.id === id);
                      if (document) {
                        handleDocViewerOpen(
                          !driver || !isDriverAssignedToTrip ? true : document.visibility,
                          document
                        );
                      }
                    }}
                  />

                  {trip &&
                    tripAttachments?.map((document, i) => (
                      <DetailFile
                        key={i}
                        fileName={document.attachment.name}
                        type={StringUtils.getLoadDocumentTypeLabel(document.type)}
                        created_at={
                          document.attachment.created_at
                            ? new Date(document.attachment.created_at)
                            : undefined
                        }
                        onClick={(): void => {
                          handleDocViewerOpen(
                            !driver || !isDriverAssignedToTrip ? true : document.visibility,
                            document
                          );
                        }}
                        isEncrypted={document.is_encrypted}
                        tag={
                          document.order_id
                            ? `Order #${showOrderTag(document.order_id)}`
                            : undefined
                        }
                      />
                    ))}
                </Box>
              </Box>
            </>
          </TabPanel>
          <TabPanel id="expenses-tab" value={loadTabs} index={1}>
            <TwExpensesTab
              tripId={trip.id}
              readableTripId={readableTripNumber}
              isDriverLink={!!driver && isDriverAssignedToTrip}
              token={token}
              driver={driver}
              updateCounterTab={updateCounterExpenseTab}
            />
          </TabPanel>
          <TabPanel id="dispatch-notes-tab" value={loadTabs} index={2}>
            <TwDispatchNotes
              tripId={trip.id}
              orderId={order?.id}
              isSplitLoad={true}
              isDriverLink={!!driver && isDriverAssignedToTrip}
              token={token}
              reloadData={reloadData}
              loadReadableId={order?.readable_id || 0}
              driverId={driver.id}
              componentRef={dispatchNotesRef}
              ltlEnabled={isLtlEnabled}
            />
          </TabPanel>
        </Box>
      );
    } else {
      return (
        <Box component="div" className="load-data-container__general-data">
          <Box component="div" className="title">
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-start"
              alignItems="center"
              sx={{ width: '100%', marginBottom: '20px' }}
            >
              <span>Route</span>
            </Stack>
          </Box>

          {trip && (
            <SegmentRouteInfo
              segment={trip}
              allSegments={order?.trips || [trip]}
              startOrder={stopStartOrder}
              showStatusUpdateInput={false}
              showAddSignatureAction={!!driver && isDriverAssignedToTrip}
              isCustomerLink={!driver || !isDriverAssignedToTrip}
              publicLoadToken={token}
            />
          )}

          {showCommodities && (
            <TwLoadCommodityReadOnly
              commodities={getCommodities()}
              entity={EntityType.TRIP}
              disableActions={true}
            />
          )}

          <Box component="div" className="title">
            Equipment
          </Box>
          <Box component="div" className="equipment">
            <Box component="div" className="equipment__item">
              <Box component="span">Truck name or #</Box>
              <Box component="span">{trip.truck?.name}</Box>
            </Box>

            <Box component="div" className="equipment__item">
              <Box component="span">Trailer name</Box>
              <Box component="span">{trip.trailer?.name}</Box>
            </Box>
          </Box>
        </Box>
      );
    }
  };

  const showTabsContainerMobile = (): JSX.Element => {
    if (driver && driver.id && isDriverAssignedToTrip) {
      return (
        <Box
          component="section"
          className="tabs-container"
          sx={{
            paddingLeft: '20px',
            paddingRight: '20px'
          }}
        >
          <TwLoadDetailTabsGroup
            value={loadTabs}
            onChange={handleLoadDetailsTabChange}
            aria-label="TwTabs"
            role="TwTabs"
          >
            <TwLoadDetailTab label="Trip overview" />
            <TwLoadDetailTab label="Attachments" />
            <TwLoadDetailTab
              label="Expenses"
              icon={<Chip sx={{ height: '20px' }} label={trip.other_expenses_count_by_role || 0} />}
              iconPosition="end"
            />
            <TwLoadDetailTab
              label="Notes"
              icon={<Chip sx={{ height: '20px' }} label={notes?.length || 0} />}
              iconPosition="end"
            />
          </TwLoadDetailTabsGroup>

          <TabPanel id="load-detail-tab" value={loadTabs} index={0}>
            <>
              <Accordion
                expanded={!!expandedItems['route']}
                onChange={(): void => handleAccordionExpandedChange('route')}
              >
                <AccordionSummary aria-controls="route-content">Route</AccordionSummary>
                <AccordionDetails>
                  {trip && (
                    <SegmentRouteInfo
                      segment={trip}
                      allSegments={order?.trips || [trip]}
                      startOrder={stopStartOrder}
                      loadId={order?.id || ''}
                      showStatusUpdateInput={false}
                      showAddSignatureAction={!!driver && isDriverAssignedToTrip}
                      reloadData={(): void => {
                        reloadData && reloadData();
                      }}
                      publicLoadToken={token}
                      isCustomerLink={!driver || !isDriverAssignedToTrip}
                    />
                  )}
                </AccordionDetails>
              </Accordion>

              {showCommodities && (
                <Accordion
                  expanded={!!expandedItems['commodity']}
                  onChange={(): void => handleAccordionExpandedChange('commodity')}
                >
                  <AccordionSummary aria-controls="commodity-content">Commodity</AccordionSummary>
                  <AccordionDetails>
                    <TwLoadCommodityReadOnly
                      commodities={getCommodities()}
                      entity={EntityType.TRIP}
                      showTitle={false}
                      overlayView={isLtlEnabled}
                      disableActions={true}
                    />
                  </AccordionDetails>
                </Accordion>
              )}

              <Accordion
                expanded={!!expandedItems['instructions']}
                onChange={(): void => handleAccordionExpandedChange('instructions')}
              >
                <AccordionSummary aria-controls="special-instructions">Trip notes</AccordionSummary>
                <AccordionDetails>
                  <Box component="div" className="special-instructions">
                    {formatUrlsInText(trip.instructions || '')}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={!!expandedItems['equipment']}
                onChange={(): void => handleAccordionExpandedChange('equipment')}
              >
                <AccordionSummary aria-controls="equipment">Equipment</AccordionSummary>
                <AccordionDetails>
                  <Box component="div" className="equipment">
                    <TwEquipmentField
                      driversEditEquipmentEnabled={driversEditEquipmentEnabled}
                      equipmentType={EquipmentType.truck}
                      userRole={USER_ROLE.DRIVER}
                      segmentId={trip.id}
                      token={token}
                    />
                    <TwEquipmentField
                      driversEditEquipmentEnabled={driversEditEquipmentEnabled}
                      equipmentType={EquipmentType.trailer}
                      userRole={USER_ROLE.DRIVER}
                      segmentId={trip.id}
                      token={token}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={!!expandedItems['overview']}
                onChange={(): void => handleAccordionExpandedChange('overview')}
              >
                <AccordionSummary aria-controls="overview">Overview</AccordionSummary>
                <AccordionDetails>
                  {enableFlexibleTagging && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Tags
                      </Box>
                      <TwLoadTags
                        entity={EntityType.TRIP}
                        loadId={order?.id || ''}
                        tripId={trip.id || ''}
                        tags={trip?.tags || []}
                        readOnly
                        containerSx={{ marginBottom: '1rem' }}
                      />
                    </>
                  )}

                  {trip.drivers &&
                    trip.drivers.length > 0 &&
                    trip.drivers.map((driverItem, i) => (
                      <React.Fragment key={driverItem.id}>
                        <Box component="div" className="overview--sub-title">
                          {trip.drivers?.length === 1 ? `Driver` : `Driver ${i + 1}`}
                        </Box>
                        <Box component="div" className="overview__driver">
                          <TwInitialsIndicator
                            text={`${driverItem.first_name} ${driverItem.last_name}`}
                          />
                          <Box component="div" className="overview__driver--data">
                            <Box component="span">{`${driverItem.first_name} ${driverItem.last_name}`}</Box>

                            <Box component="span">
                              {driverItem.phone
                                ? StringUtils.formatPhoneNumber(driverItem.phone.toString())
                                : driverItem.email_address}
                            </Box>
                          </Box>
                        </Box>
                        {shouldShowGrossPay(driverItem) && (
                          <>
                            <Box component="div" className="overview--sub-title">
                              {trip.drivers?.length === 1
                                ? `Driver pay estimate`
                                : `Driver ${i + 1} pay estimate`}
                            </Box>

                            <Box
                              component="div"
                              className="load-detail__data-container__overview__rate"
                              style={{ display: 'flex', marginBottom: '5%' }}
                            >
                              {/* We need to add the other pay items total amount to the driver pay override,
                            to simulate the sum which will be re-calculated in the BE when the load is saved */}
                              {driverItem.driver_pay_override_data?.gross_pay_estimate &&
                                NumberUtils.getCurrency(
                                  driverItem.driver_pay_override_data.gross_pay_estimate +
                                    DriverPayUtils.getOtherPayItemsTotalAmount(
                                      driverItem.trip_driver_pay_other_payment || []
                                    )
                                )}
                              {driverItem.driver_pay_override_data && (
                                <TwGrossPayTooltip
                                  numberOfDrivers={trip.drivers.length}
                                  loadType={LOAD_TYPE.SEGMENT}
                                  grossPayData={driverItem.driver_pay_override_data}
                                  otherPayItemsTotalAmount={DriverPayUtils.getOtherPayItemsTotalAmount(
                                    driverItem.trip_driver_pay_other_payment || []
                                  )}
                                />
                              )}
                            </Box>
                          </>
                        )}
                      </React.Fragment>
                    ))}

                  {driver && driver.id && driversToEditMilesEnabled && (
                    <TwTotalMilage
                      totalMileage={totalMileage}
                      emptyMileage={trip.empty_miles || 0}
                      loadedMileage={trip.estimated_mileage || 0}
                      tooltipText={
                        trip.is_empty_miles_auto
                          ? 'Calculated based on last known location to the first stop'
                          : 'Manually calculated'
                      }
                      tripId={trip.id}
                      reloadData={reloadData}
                      isDriver={!!driver}
                      isEmptyMilesEnabled={isEmptyMilesEnabled}
                      token={token}
                      editable={!trip.has_driver_settlement}
                    />
                  )}

                  {isEnabledCarrier && trip.carrier && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Carrier
                      </Box>
                      <Box component="div" className="overview__client-load">
                        {order && trip.carrier.name}
                      </Box>
                    </>
                  )}
                  <Box component="div" className="overview--sub-title">
                    Trip Status
                  </Box>
                  <Box component="div" className="overview__driver">
                    {trip.status ? LoadUtils.getTripStatusReadableText(trip.status) : ''}
                  </Box>
                  <Box component="div" className="overview--sub-title">
                    Trip #
                  </Box>
                  <Box component="div" className="overview__driver">
                    {readableId}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          </TabPanel>
          <TabPanel id="attachments-tab" value={loadTabs} index={1}>
            <Accordion
              expanded={!!expandedItems['attachments']}
              onChange={(): void => handleAccordionExpandedChange('attachments')}
            >
              <AccordionSummary aria-controls="attachments-content">
                {`${tripAttachments.length || 0} Attachments`}
              </AccordionSummary>
              <AccordionDetails>
                <Box component="div" className="attachments">
                  {driver && driver.id && isDriverAssignedToTrip && !matchesMobileOrTablet && (
                    <>
                      <Box
                        className="attachments--container-2"
                        onClick={(): void =>
                          matchesMobileOrFlutterApp ? openScanner() : setDocumentsOpen(true)
                        }
                      >
                        <CloudUploadIcon
                          className="attachments--icon"
                          sx={{ marginRight: '10px' }}
                        />
                        <p className="attachments--help-text">
                          <span className="attachments--highlited-text">
                            {DROP_ZONE_HIGHLIGHTED_TEXT}
                          </span>
                        </p>
                      </Box>

                      {fileUploadErrors.length > 0 &&
                        fileUploadErrors.map((error, i) => <TwAlert key={i} message={error} />)}
                    </>
                  )}

                  {trip &&
                    tripAttachments?.map((document, i) => (
                      <DetailFile
                        key={i}
                        fileName={document.attachment.name}
                        type={StringUtils.getLoadDocumentTypeLabel(document.type)}
                        created_at={
                          document.attachment.created_at
                            ? new Date(document.attachment.created_at)
                            : undefined
                        }
                        onClick={(): void => {
                          handleDocViewerOpen(
                            !driver || !isDriverAssignedToTrip ? true : document.visibility,
                            document
                          );
                        }}
                        isEncrypted={document.is_encrypted}
                      />
                    ))}
                </Box>

                <TwDocumentsToGenerateTrip
                  files={generatedDocuments}
                  tripId={trip.id}
                  isDriverOrCarrier={true}
                  onDownload={(file): void => {
                    handleDownloadDoc(file);
                  }}
                  onClick={(id): void => {
                    const document = generatedDocuments.find((doc) => doc.id === id);
                    if (document) {
                      handleDocViewerOpen(
                        !driver || !isDriverAssignedToTrip ? true : document.visibility,
                        document
                      );
                    }
                  }}
                />
              </AccordionDetails>
            </Accordion>
          </TabPanel>
          <TabPanel id="expenses-tab" value={loadTabs} index={2}>
            <TwExpensesTab
              tripId={trip.id}
              readableTripId={readableTripNumber}
              isDriverLink={!!driver && isDriverAssignedToTrip}
              token={token}
              driver={driver}
              updateCounterTab={updateCounterExpenseTab}
            />
          </TabPanel>
          <TabPanel id="dispatch-notes-tab" value={loadTabs} index={3}>
            <TwDispatchNotes
              tripId={trip.id}
              isSplitLoad={true}
              isDriverLink={!!driver}
              token={token}
              reloadData={reloadData}
              loadReadableId={order?.readable_id || 0}
              driverId={driver.id}
              componentRef={dispatchNotesRef}
              ltlEnabled={isLtlEnabled}
            />
          </TabPanel>
        </Box>
      );
    } else {
      return (
        <Box
          component="section"
          className="tabs-container"
          sx={{
            paddingLeft: '20px',
            paddingRight: '20px'
          }}
        >
          <Accordion
            expanded={!!expandedItems['route']}
            onChange={(): void => handleAccordionExpandedChange('route')}
          >
            <AccordionSummary aria-controls="route-content">Route</AccordionSummary>
            <AccordionDetails>
              {trip && (
                <SegmentRouteInfo
                  segment={trip}
                  allSegments={order?.trips || [trip]}
                  startOrder={stopStartOrder}
                  loadId={order?.id}
                  showStatusUpdateInput={false}
                  showAddSignatureAction={!!driver}
                  reloadData={(): void => {
                    reloadData && reloadData();
                  }}
                  publicLoadToken={token}
                  isCustomerLink={!driver}
                />
              )}
            </AccordionDetails>
          </Accordion>

          {showCommodities && (
            <Accordion
              expanded={!!expandedItems['commodity']}
              onChange={(): void => handleAccordionExpandedChange('commodity')}
            >
              <AccordionSummary aria-controls="commodity-content">Commodity</AccordionSummary>
              <AccordionDetails>
                <TwLoadCommodityReadOnly
                  commodities={getCommodities()}
                  entity={EntityType.TRIP}
                  showTitle={false}
                  disableActions={true}
                />
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion
            expanded={!!expandedItems['equipment']}
            onChange={(): void => handleAccordionExpandedChange('equipment')}
          >
            <AccordionSummary aria-controls="equipment">Equipment</AccordionSummary>
            <AccordionDetails>
              <Box component="div" className="equipment">
                <Box component="div" className="equipment__item">
                  <Box component="span">Truck name or #</Box>
                  <Box component="span">{trip.truck?.name}</Box>
                </Box>

                <Box component="div" className="equipment__item">
                  <Box component="span">Trailer name</Box>
                  <Box component="span">{trip.trailer?.name}</Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={!!expandedItems['overview']}
            onChange={(): void => handleAccordionExpandedChange('overview')}
          >
            <AccordionSummary aria-controls="overview">Overview</AccordionSummary>
            <AccordionDetails>
              {trip.drivers &&
                trip.drivers.length > 0 &&
                trip.drivers.map((driverItem, i) => (
                  <React.Fragment key={driverItem.id}>
                    <Box component="div" className="overview--sub-title">
                      {trip.drivers?.length === 1 ? `Driver` : `Driver ${i + 1}`}
                    </Box>
                    <Box component="div" className="overview__driver">
                      <TwInitialsIndicator
                        text={`${driverItem.first_name} ${driverItem.last_name}`}
                      />
                      <Box component="div" className="overview__driver--data">
                        <Box component="span">{`${driverItem.first_name} ${driverItem.last_name}`}</Box>
                        {isHideDriverInfoEnabled && !driver && <></>}
                        {!!driver && (
                          <>
                            {driverItem.phone
                              ? StringUtils.formatPhoneNumber(driverItem.phone.toString())
                              : driverItem.email_address}
                          </>
                        )}
                      </Box>
                    </Box>
                    {shouldShowGrossPay(driverItem) && (
                      <>
                        <Box component="div" className="overview--sub-title">
                          {trip.drivers?.length === 1
                            ? `Driver pay estimate`
                            : `Driver ${i + 1} pay estimate`}
                        </Box>

                        <Box
                          component="div"
                          className="load-detail__data-container__overview__rate"
                          style={{ display: 'flex', marginBottom: '5%' }}
                        >
                          {/* We need to add the other pay items total amount to the driver pay override,
                              to simulate the sum which will be re-calculated in the BE when the load is saved */}
                          {driverItem.driver_pay_override_data?.gross_pay_estimate &&
                            NumberUtils.getCurrency(
                              driverItem.driver_pay_override_data.gross_pay_estimate +
                                DriverPayUtils.getOtherPayItemsTotalAmount(
                                  driverItem.trip_driver_pay_other_payment || []
                                )
                            )}
                          {driverItem.driver_pay_override_data && (
                            <TwGrossPayTooltip
                              numberOfDrivers={trip.drivers.length}
                              loadType={LOAD_TYPE.SEGMENT}
                              grossPayData={driverItem.driver_pay_override_data}
                              otherPayItemsTotalAmount={DriverPayUtils.getOtherPayItemsTotalAmount(
                                driverItem.trip_driver_pay_other_payment || []
                              )}
                            />
                          )}
                        </Box>
                      </>
                    )}
                    {isEnabledCarrier && trip.carrier && (
                      <>
                        <Box component="div" className="overview--sub-title">
                          Carrier
                        </Box>
                        <Box component="div" className="overview__client-load">
                          {order && trip.carrier.name}
                        </Box>
                      </>
                    )}
                    <Box component="div" className="overview--sub-title">
                      Trip Status
                    </Box>
                    <Box component="div" className="overview__client-load">
                      {trip.status ? LoadUtils.getTripStatusReadableText(trip.status) : ''}
                    </Box>
                    <Box component="div" className="overview--sub-title">
                      Trip #
                    </Box>
                    <Box component="div" className="overview__client-load">
                      {`${order?.readable_id}-${segmentOrder + 1}`}
                    </Box>
                  </React.Fragment>
                ))}
            </AccordionDetails>
          </Accordion>
        </Box>
      );
    }
  };

  const orderOptions = useMemo(() => {
    let options = [] as any[];

    if (trip?.orders && trip.orders.length > 0) {
      options = trip.orders.map((order) => ({
        value: order.id,
        label: order.readable_id as unknown as string,
        customer: order.raw_customer?.name || '',
        customerOrderNumber: order.customer_order_number,
        readableId: order.readable_id
      }));
    }

    options.push({
      value: '',
      label: '-',
      customer: 'Do not assign order'
    });

    return options;
  }, [trip]);

  const totalMileage = useMemo(() => {
    let total = 0;
    if (trip.estimated_mileage) {
      total += trip.estimated_mileage;
    }
    if (trip.empty_miles && isEmptyMilesEnabled) {
      total += trip.empty_miles;
    }

    return total;
  }, [trip.estimated_mileage, trip.empty_miles]);

  const updateCounterExpenseTab = (): void => {
    reloadData && reloadData();
  };

  const showAcceptRejectButtons = useMemo(() => {
    if (!enableDriverAcknowledge) return false;
    if (!isDriverAssignedToTrip) return false;
    if (!isDriverAckSettingOn) return false;
    if (!tenantDriverSettings) return false;
    if (!trip) return false;
    if (!driver) return false;

    const tripDriver = trip.drivers.find((d) => d.id === driver.id);

    if (!tripDriver) return false;

    // Company driver
    if (tripDriver.type === DRIVER_TYPE.COMPANY_DRIVER || !tripDriver.type) {
      // if company driver ack type setting is not ACCEPT_REJECT, then don't show the modal
      if (tenantDriverSettings.company_driver_ack_type !== DriverAckType.ACCEPT_REJECT) {
        return false;
      }

      const { driver_ack_status } = tripDriver;

      // if trip driver ack status is ACCEPTED or CONFIRMED, then don't show the modal
      if (
        driver_ack_status === TripDriverAckStatus.ACCEPTED ||
        driver_ack_status === TripDriverAckStatus.CONFIRMED ||
        driver_ack_status === TripDriverAckStatus.REJECTED
      ) {
        return false;
      }

      if (driver_ack_status === null || driver_ack_status === TripDriverAckStatus.SENT) {
        setShowAcceptRejectSection(true);
      }
    }

    // Owner operator driver
    if (tripDriver.type === DRIVER_TYPE.OWNER_OPERATOR_DRIVER) {
      // if owner operator driver ack type setting is not CONFIRMATION, then don't show the buttons
      if (
        tenantDriverSettings.owner_operator_driver_ack_type !==
        OwnerOperatorDriverAckType.ACCEPT_REJECT
      ) {
        return false;
      }

      const { driver_ack_status } = tripDriver;

      // if trip driver ack status is ACCEPTED, CONFIRMED, or REJECTED, then don't show the buttons
      if (
        driver_ack_status === TripDriverAckStatus.ACCEPTED ||
        driver_ack_status === TripDriverAckStatus.CONFIRMED ||
        driver_ack_status === TripDriverAckStatus.REJECTED
      ) {
        return false;
      }

      if (driver_ack_status === null || driver_ack_status === TripDriverAckStatus.SENT) {
        setShowAcceptRejectSection(true);
      }
    }

    return true;
  }, [
    enableDriverAcknowledge,
    isDriverAssignedToTrip,
    tenantDriverSettings,
    isDriverAckSettingOn,
    tenantDriverSettings,
    trip,
    driver
  ]);

  return (
    <>
      <Box component="section" className="sub-header">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {primaryClient && (
            <Box component="div" className="sub-header__user-info">
              <TwInitialsIndicator
                text={(primaryClient?.initials ?? primaryClient?.name) || ''}
                displayRawText={!primaryClient?.initials}
              />
              <Box
                component="div"
                className="sub-header__user-info--data"
                sx={{
                  justifyContent: 'center'
                }}
              >
                <Box component="span">
                  {primaryClient.name}
                  {customersCount && customersCount > 1 && `, +${customersCount - 1}`}
                </Box>
              </Box>
            </Box>
          )}

          {matchesMobileOrTablet && trip.status && trip.status !== TRIP_STATUS.DRAFT && (
            <TwBadge
              content={StringUtils.getSegmentStatusLabel(trip.status)}
              sx={{
                color: `#fff !important`,
                backgroundColor: `${TripStatusColor[trip.status]} !important`
              }}
              className="tid-load-status"
            />
          )}
        </Box>

        {trip &&
          statusLabelButton &&
          !loading &&
          driver &&
          driver.id &&
          isDriverAssignedToTrip &&
          !showAcceptRejectButtons && (
            <TwButton
              type={TwButtonType.primary}
              label={statusLabelButton}
              action={handleStopStatusProgression}
              fullwidth={matchesMobileOrTablet}
            />
          )}

        {showAcceptRejectButtons && driver && showAcceptRejectSection && (
          <TwAcceptRejectButtons
            driverId={driver.id}
            tripId={trip.id}
            orderId={order?.id}
            onDisappear={(): void => {
              setShowAcceptRejectSection(false);
              reloadData && reloadData();
            }}
            token={token}
          />
        )}

        {matchesMobileOrTablet &&
          driver &&
          driver.id &&
          isDriverAssignedToTrip &&
          !showAcceptRejectButtons &&
          (enableDynamicWebTwain ? (
            <TwButton
              type={TwButtonType.secondary}
              label={'Upload docs or photos'}
              action={openScanner}
              fullwidth={matchesMobileOrTablet}
              className={'dynamicwt-button'}
            />
          ) : (
            <>
              <input
                type="file"
                id="upload"
                accept="application/pdf image/png image/jpeg image/heic"
                hidden
                onChange={handleChangeUploadDocument}
              />
              <label className="mobile-upload" htmlFor="upload">
                Upload document
              </label>
            </>
          ))}
      </Box>

      {matchesMobileOrTablet && showTabsContainerMobile()}

      {!matchesMobileOrTablet && (
        <Box component="section" className="load-data-container">
          {showGeneralDataSectionDesktop()}

          <Box component="div" className="load-data-container__overview-data">
            <Box component="div" className="title">
              Overview
            </Box>
            {enableFlexibleTagging && !!driver && (
              <>
                <Box component="div" className="overview--sub-title">
                  Tags
                </Box>
                <TwLoadTags
                  entity={EntityType.TRIP}
                  loadId={order?.id || ''}
                  tripId={trip.id || ''}
                  tags={trip?.tags || []}
                  readOnly
                  containerSx={{ marginBottom: '1rem' }}
                />
              </>
            )}
            {trip.drivers &&
              trip.drivers.length > 0 &&
              trip.drivers.map((driverItem, i) => (
                <React.Fragment key={driverItem.id}>
                  <Box component="div" className="overview--sub-title">
                    {trip.drivers?.length === 1 ? `Driver` : `Driver ${i + 1}`}
                  </Box>
                  <Box component="div" className="overview__driver">
                    <TwInitialsIndicator
                      text={`${driverItem.first_name} ${driverItem.last_name}`}
                    />
                    <Box component="div" className="overview__driver--data">
                      <Box component="span">{`${driverItem.first_name} ${driverItem.last_name}`}</Box>
                      <Box component="span">
                        {isHideDriverInfoEnabled && !driver && <></>}
                        {!!driver && (
                          <>
                            {driverItem.phone
                              ? StringUtils.formatPhoneNumber(driverItem.phone.toString())
                              : driverItem.email_address}
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {shouldShowGrossPay(driverItem) && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        {trip.drivers?.length === 1
                          ? `Driver pay estimate`
                          : `Driver ${i + 1} pay estimate`}
                      </Box>

                      <Box
                        component="div"
                        className="load-detail__data-container__overview__rate"
                        style={{ display: 'flex', marginBottom: '5%' }}
                      >
                        {/* We need to add the other pay items total amount to the driver pay override,
                        to simulate the sum which will be re-calculated in the BE when the load is saved */}
                        {NumberUtils.getCurrency(
                          (driverItem.driver_pay_override_data?.gross_pay_estimate || 0) +
                            DriverPayUtils.getOtherPayItemsTotalAmount(
                              driverItem.trip_driver_pay_other_payment || []
                            )
                        )}

                        {driverItem.driver_pay_override_data && (
                          <TwGrossPayTooltip
                            numberOfDrivers={trip.drivers.length}
                            loadType={LOAD_TYPE.SEGMENT}
                            grossPayData={driverItem.driver_pay_override_data}
                            otherPayItemsTotalAmount={DriverPayUtils.getOtherPayItemsTotalAmount(
                              driverItem.trip_driver_pay_other_payment || []
                            )}
                          />
                        )}
                      </Box>
                    </>
                  )}
                </React.Fragment>
              ))}
            {driver && driver.id && driversToEditMilesEnabled && (
              <TwTotalMilage
                totalMileage={totalMileage}
                emptyMileage={trip.empty_miles || 0}
                loadedMileage={trip.estimated_mileage || 0}
                tooltipText={
                  trip.is_empty_miles_auto
                    ? 'Calculated based on last known location to the first stop'
                    : 'Manually calculated'
                }
                tripId={trip.id}
                reloadData={reloadData}
                isDriver={!!driver}
                isEmptyMilesEnabled={isEmptyMilesEnabled}
                token={token}
                editable={!trip.has_driver_settlement}
              />
            )}
            {isEnabledCarrier && trip.carrier && (
              <>
                <Box component="div" className="overview--sub-title">
                  Carrier
                </Box>
                <Box component="div" className="overview__client-load">
                  {order && trip.carrier.name}
                </Box>
              </>
            )}
            <Box component="div" className="overview--sub-title">
              Trip Status
            </Box>
            <Box component="div" className="overview__client-load">
              {trip.status ? LoadUtils.getTripStatusReadableText(trip.status) : ''}
            </Box>
            <Box component="div" className="overview--sub-title">
              Trip #
            </Box>
            <Box component="div" className="overview__client-load">
              {readableId}
            </Box>
          </Box>
        </Box>
      )}

      <TwUploadOrderTripFiles
        open={documentsOpen}
        orderId={order?.id || ''}
        tripId={trip.id || ''}
        entity="trip"
        defaultVisibility={true}
        handleClose={(): void => setDocumentsOpen(false)}
        entityList={orderOptions}
        onAddDocumentsSuccess={reloadData}
        disableInternalUpload={false}
        guestServiceToken={token}
        entityStatus={trip.status}
      />

      <TwDeleteOrderTripDoc
        open={showDelete}
        fileName={selectedDocument?.attachment.name || ''}
        onClickClose={handleCloseDeleteDoc}
        onDelete={handleDeleteDoc}
      />

      {matchesMobileOrFlutterApp && enableDynamicWebTwain && trip && (
        <TwScanner
          isOpen={isTwScannerOpen}
          closeMenu={(): void => setIsTwScannerOpen(false)}
          onClose={closeScanner}
          onUploadSuccess={handleDocumentsUpload}
          orderId={order?.id || ''}
          tripId={trip.id || ''}
          defaultVisibility={true}
          entityList={orderOptions}
          entity="trip"
          token={token}
          ltlEnabled={isLtlEnabled}
          readableTripId={readableTripNumber}
          isDriverLink={!!driver && isDriverAssignedToTrip}
          driver={driver}
          updateCounterTab={updateCounterExpenseTab}
        />
      )}

      {order && (
        <UploadBol
          loadId={order.id}
          open={isBolUploadOpen}
          onClose={handleBolUploadClose}
          onSuccess={handleBolUpload}
          guestServiceToken={token}
          customUploadHandler={
            matchesMobileOrFlutterApp && enableDynamicWebTwain ? openScanner : undefined
          }
        />
      )}

      {/** Document and photo preview */}
      {trip && trip.trip_attachments && (
        <TwOrderTripsDocViewer
          entity="Trip"
          readableId={trip?.readable_id}
          selectedFileId={selectedDocument?.id}
          files={filesToPreview}
          open={docViewOpen}
          onClickClose={(): void => {
            setDocViewOpen(false);
            setSelectedDocument(null);
          }}
          onDelete={(doc): void => {
            setDocViewOpen(false);
            setSelectedDocument(doc);

            setShowDelete(true);
          }}
          isPublic={true}
          driverId={driver ? driver.id : undefined}
          token={token}
        />
      )}

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 10
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default TripPublicDetail;

const TabPanel: FC<TabPanelProps> = ({ children, index, value, id, ...restProps }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tab-panel-${index}`}
    aria-labelledby={`tab-panel-${index}`}
    {...restProps}
  >
    {value === index && <Box sx={{ paddingLeft: '20px', paddingRight: '20px' }}>{children}</Box>}
  </div>
);

const Accordion = styled((props: AccordionProps) => (
  <MUIAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  '&:not(:last-child)': {
    borderBottom: 'none'
  },
  '&:before': {
    display: 'none'
  }
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MUIAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '14px' }} />}
    {...props}
  />
))(() => ({
  padding: 0,
  '& .MuiAccordionSummary-content': {
    fontStyle: 'normal',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '27px',
    letterSpacing: '0.1px',
    color: '#171E28'
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)'
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)'
  }
}));

const AccordionDetails = styled(MUIAccordionDetails)(() => ({
  padding: 0,
  fontFamily: 'Poppins, sans-serif'
}));
